import { useState, forwardRef } from "react";
import type {
    ChangeEventHandler,
    ClipboardEventHandler,
    FC,
    FormEvent,
    ChangeEvent,
    ClipboardEvent,
    ForwardedRef,
    MouseEvent,
} from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { Box } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";

import { AutoComplete } from "../Tooltip/AutoComplete/AutoComplete";
import { KeywordItem } from "../Tooltip/AutoComplete/Stateless";

import { AddItemInputTitle } from "./AddItemInputTitle";
import { filterUserInput } from "./list-editor.utils";

const WizardAddButtonContainer = styled.div`
    position: absolute;
    top: 16px;
    right: 12px;
    width: 24px;
    transition: color 0.2s linear;
    cursor: pointer;
`;

const Divider = styled.div`
    margin: 0 16px;
`;

type Item = {
    value: string;
};

interface IListItemsInputViewProps {
    inputRef: ForwardedRef<AutoComplete>;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onPaste: ClipboardEventHandler<HTMLInputElement>;
    inputText: string;
    itemsListTypeKey?: string;
    onSuggestionSelect?: (item: Item) => void;
    onSubmit?: (e?: MouseEvent<HTMLElement> | FormEvent<HTMLFormElement>) => void;
}

export const ListItemsInputView: FC<IListItemsInputViewProps> = ({
    inputRef,
    onChange,
    onPaste,
    inputText,
    itemsListTypeKey = "KeywordAnalysis.keywordgroup.wizard.list.type",
    onSuggestionSelect,
    onSubmit,
}) => {
    const emptyText = inputText.length === 0;

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit?.(e);
    };

    return (
        <div className="customCategoriesWizard-editor-add-domain add-list-item">
            <Box
                as="form"
                margin="0"
                onSubmit={handleSubmit}
            >
                <AutoComplete
                    hasKeywordGroupsInSuggestions={false}
                    itemComponent={KeywordItem}
                    popupClass="keyword-suggestions"
                    onSelect={onSuggestionSelect}
                    ref={inputRef}
                >
                    <input
                        className="add-new-list-item"
                        onChange={onChange}
                        onPaste={onPaste}
                        aria-label="add-new-list-item"
                        tabIndex={2}
                        value={inputText}
                        type="text"
                    />
                </AutoComplete>
                {emptyText ? (
                    <AddItemInputTitle itemsListType={translate(itemsListTypeKey)} />
                ) : (
                    <WizardAddButtonContainer onClick={onSubmit}>
                        <SWReactIcons
                            iconName="add"
                            size="sm"
                        />
                    </WizardAddButtonContainer>
                )}
                <Divider className="customCategoriesWizard-add-domain-bar" />
            </Box>
        </div>
    );
};

interface IListItemsInputProps {
    onAddItem: (text: string) => void;
    onPaste: (text: string) => void;
    itemsListTypeKey?: string;
}

export const ListItemsInput = forwardRef<AutoComplete, IListItemsInputProps>(
    ({ onAddItem, onPaste, itemsListTypeKey }, ref) => {
        const [inputText, setInputText] = useState("");

        const addItem = (text: string): void => {
            if (text.length) {
                onAddItem(filterUserInput(text));
                setInputText("");
            }
        };

        const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
            setInputText(e.target.value);
        };

        const onSubmit = (e: FormEvent | MouseEvent): void => {
            if (e) {
                e.preventDefault();
            }

            addItem(inputText);
        };

        const handlePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
            e.preventDefault();
            const text = e.clipboardData.getData("Text");
            onPaste(filterUserInput(text));
        };

        const onSuggestionSelect = (item: Item): void => {
            addItem(item.value);
        };

        return (
            <ListItemsInputView
                onSuggestionSelect={onSuggestionSelect}
                inputRef={ref}
                onChange={onChange}
                onPaste={handlePaste}
                inputText={inputText}
                itemsListTypeKey={itemsListTypeKey}
                onSubmit={onSubmit}
            />
        );
    },
);
